/**
 * <pickup-scheduler>
 * Per-store Click & Collect pickup date + optional time window.
 * Reads config from retail_store metaobjects (#store-pickup-data) for the
 * customer's selected store (localStorage). Persists cart attributes.
 */
class PickupScheduler extends HTMLElement {
  constructor() {
    super();
    this.dateInput = this.querySelector('[data-pickup-date]');
    this.timeSelect = this.querySelector('[data-pickup-time]');
    this.timeField = this.querySelector('[data-pickup-time-field]');
    this.phoneInput = this.querySelector('[data-customer-phone]');
    this.phoneError = this.querySelector('[data-customer-phone-error]');
    this.status = this.querySelector('[data-pickup-status]');
    this.storeLabel = this.querySelector('[data-pickup-store-label]');
    this.storeRow = this.querySelector('[data-pickup-store-row]');
    this.noStorePrompt = this.querySelector('[data-pickup-no-store]');
    this.preferredStoreInput = this.querySelector('[data-preferred-store]');
    this.datePicker = this.querySelector('[data-pickup-date-picker]');
    this.datePickerMonth = this.querySelector('[data-pickup-date-month]');
    this.datePickerDays = this.querySelector('[data-pickup-date-days]');
    this.datePickerPrev = this.querySelector('[data-pickup-date-prev]');
    this.datePickerNext = this.querySelector('[data-pickup-date-next]');
    this.storeSelect = this.querySelector('[data-pickup-store-select]');
    this.viewMonth = null;
    this.required = this.hasAttribute('data-required');
    this.closedMessage = 'Please choose an available pickup day.';
    this.noStoreMessage = 'Select a store to choose your pickup date.';
    this.currentStore = null;
    this.availabilityTimer = null;
  }

  connectedCallback() {
    this.onStoreChanged = (event) => {
      const store = event.detail?.store ?? localStorage.getItem('selected-store');
      const previousHandle = this.currentStore?.handle || null;

      if ( store ) {
        localStorage.setItem('selected-store', store);
      }

      const resetSelections = ! store || store !== previousHandle;
      this.applyStoreConfig(resetSelections);
    };

    this.bindStoreSelect();
    this.applyStoreConfig();
    document.addEventListener('storechanged', this.onStoreChanged);

    this.datePickerPrev?.addEventListener('click', () => this.changeMonth(-1));
    this.datePickerNext?.addEventListener('click', () => this.changeMonth(1));

    if ( this.timeSelect ) {
      this.timeSelect.addEventListener('change', () => {
        this.saveAttribute('Pickup time', this.timeSelect.value);
        if ( this.timeSelect.value ) this.setStatus('');
        this.notifyChange();
      });
    }

    if ( this.phoneInput ) {
      this.phoneInput.addEventListener('input', () => {
        this.validatePhone(this.phoneInput.getAttribute('aria-invalid') === 'true');
        this.notifyChange();
      });
      this.phoneInput.addEventListener('change', () => {
        this.phoneInput.value = this.phoneInput.value.trim();
        this.validatePhone(true);
        this.saveAttribute('Customer Phone Number', this.phoneInput.value);
        this.notifyChange();
      });
      this.validatePhone(false);
    }

    this.restoreSavedTime();

    this.availabilityTimer = window.setInterval(() => {
      this.refreshAvailability();
    }, 60000);
  }

  disconnectedCallback() {
    document.removeEventListener('storechanged', this.onStoreChanged);
    if ( this.availabilityTimer ) {
      window.clearInterval(this.availabilityTimer);
      this.availabilityTimer = null;
    }
  }

  bindStoreSelect() {
    if ( ! this.storeSelect ) {
      return;
    }

    const handle = this.getSelectedHandle();
    if ( handle ) {
      this.storeSelect.value = handle;
    }

    this.storeSelect.addEventListener('change', () => {
      const nextHandle = this.storeSelect.value;
      if ( ! nextHandle ) {
        return;
      }

      localStorage.setItem('selected-store', nextHandle);
      document.dispatchEvent(new CustomEvent('storechanged', {
        detail: { store: nextHandle },
      }));
    });
  }

  restoreSavedTime() {
    if ( ! this.timeSelect ) {
      return;
    }

    const savedOption = this.timeSelect.querySelector('option[selected]');
    const savedTime = savedOption?.value || '';

    if ( savedTime && this.timeSelect.querySelector(`option[value="${CSS.escape(savedTime)}"]`) ) {
      this.timeSelect.value = savedTime;
    }
  }

  refreshAvailability() {
    if ( ! this.currentStore || ! this.dateInput?.value || typeof StoreHoursUtils === 'undefined' ) {
      return;
    }

    const earliestISO = StoreHoursUtils.toISO(StoreHoursUtils.firstAvailablePickupDate(this.currentStore));
    const selectedDate = this.dateInput.value;

    if ( selectedDate < earliestISO || StoreHoursUtils.isPickupClosedOnDate(this.currentStore, StoreHoursUtils.parseISODate(selectedDate)) ) {
      this.applyStoreConfig(false);
      return;
    }

    const windows = this.getTimeWindowsForSelectedDate();
    const selectedTime = this.timeSelect?.value || '';
    const timeIsInvalid = this.currentStore.time_windows?.length
      && (! windows.length || (selectedTime && ! windows.includes(selectedTime)));

    if ( timeIsInvalid ) {
      this.rebuildTimeWindows(windows, false);
      this.setStatus(this.closedMessage);
      this.notifyChange();
      return;
    }

    this.updatePageCheckout();
  }

  getTimeWindowsForSelectedDate(now = new Date()) {
    if ( ! this.currentStore ) {
      return [];
    }

    const allWindows = this.currentStore.time_windows || [];
    const dateISO = this.dateInput?.value;

    if ( ! dateISO || typeof StoreHoursUtils === 'undefined' ) {
      return allWindows;
    }

    return StoreHoursUtils.filterTimeWindows(this.currentStore, dateISO, allWindows, now);
  }

  getSelectedHandle() {
    return localStorage.getItem('selected-store');
  }

  isPhoneValid() {
    return Boolean(
      this.phoneInput?.value.trim()
      && window.BFFPhoneValidation?.isValidAustralianPhoneNumber(this.phoneInput.value),
    );
  }

  validatePhone(showError = true) {
    if ( ! this.phoneInput ) {
      return false;
    }

    const value = this.phoneInput.value.trim();
    const valid = this.isPhoneValid();
    const showInvalid = showError && Boolean(value) && ! valid;
    const message = value && ! valid
      ? 'Enter a valid Australian mobile or landline number.'
      : '';

    this.phoneInput.setCustomValidity(message);
    this.phoneInput.toggleAttribute('aria-invalid', showInvalid);

    if ( this.phoneError ) {
      this.phoneError.hidden = ! showInvalid;
    }

    return valid;
  }

  isComplete() {
    const store = this.currentStore || this.resolveCurrentStore();

    if ( ! store ) {
      return false;
    }

    if ( ! this.dateInput?.value ) {
      return false;
    }

    if ( ! this.isPhoneValid() ) {
      return false;
    }

    if ( typeof StoreHoursUtils !== 'undefined' && StoreHoursUtils.isPickupClosedOnDate(store, StoreHoursUtils.parseISODate(this.dateInput.value)) ) {
      return false;
    }

    if ( typeof StoreHoursUtils !== 'undefined' ) {
      const earliestISO = StoreHoursUtils.toISO(StoreHoursUtils.firstAvailablePickupDate(store));
      if ( this.dateInput.value < earliestISO ) {
        return false;
      }
    }

    const availableWindows = this.getTimeWindowsForSelectedDate();
    if ( store.time_windows?.length && ! availableWindows.length ) {
      return false;
    }
    if ( availableWindows.length && this.required && ! this.timeSelect?.value ) {
      return false;
    }

    if ( availableWindows.length && this.timeSelect?.value && ! availableWindows.includes(this.timeSelect.value) ) {
      return false;
    }

    return true;
  }

  getSummary() {
    const store = this.currentStore || this.resolveCurrentStore();

    if ( ! store ) {
      return { noStore: true, complete: false, text: '', storeLine: '', scheduleLine: '' };
    }

    const storeLine = `Pickup at ${store.name}`;

    if ( ! this.isComplete() ) {
      let scheduleLine = '';

      if ( this.dateInput?.value ) {
        const dateFormatted = typeof StoreHoursUtils !== 'undefined'
          ? StoreHoursUtils.formatPickupDate(this.dateInput.value, store.timezone)
          : this.dateInput.value;
        scheduleLine = dateFormatted;

        if ( this.timeSelect?.value ) {
          scheduleLine += ` · ${this.timeSelect.value}`;
        }
      }

      const text = scheduleLine ? `${storeLine} · ${scheduleLine}` : storeLine;

      return {
        complete: false,
        text,
        storeLine,
        scheduleLine,
        storeName: store.name,
      };
    }

    const date = this.dateInput.value;
    const time = this.timeSelect?.value || '';
    const dateFormatted = typeof StoreHoursUtils !== 'undefined'
      ? StoreHoursUtils.formatPickupDate(date, store.timezone)
      : date;
    let scheduleLine = dateFormatted;

    if ( time ) {
      scheduleLine += ` · ${time}`;
    }

    const text = `${storeLine} · ${scheduleLine}`;

    return {
      complete: true,
      text,
      storeLine,
      scheduleLine,
      storeName: store.name,
      date,
      time,
    };
  }

  resolveCurrentStore() {
    if ( typeof StoreHoursUtils === 'undefined' ) {
      return null;
    }

    const handle = this.getSelectedHandle();
    return StoreHoursUtils.getPickupStore(handle);
  }

  notifyChange() {
    this.dispatchEvent(new CustomEvent('pickupchange', {
      bubbles: true,
      detail: {
        complete: this.isComplete(),
        summary: this.getSummary(),
        context: this.dataset.context || 'page',
      },
    }));
    this.updatePageCheckout();
  }

  updatePageCheckout() {
    const checkoutBtn = document.querySelector('#CheckOut[data-pickup-checkout]');
    if ( ! checkoutBtn || checkoutBtn.closest('#site-cart-sidebar') ) {
      return;
    }

    if ( ! this.required ) {
      return;
    }

    const termsCheckbox = document.querySelector('#AgreeToTos');
    const termsOk = ! termsCheckbox || termsCheckbox.checked;
    checkoutBtn.disabled = ! this.isComplete() || ! termsOk;
  }

  applyStoreConfig(resetSelections = false, now = new Date()) {
    if ( typeof StoreHoursUtils === 'undefined' ) {
      return;
    }

    const handle = this.getSelectedHandle();
    this.currentStore = StoreHoursUtils.getPickupStore(handle);

    if ( ! this.currentStore ) {
      this.showNoStoreState();
      this.notifyChange();
      return;
    }

    this.showStoreState(this.currentStore);

    const earliest = StoreHoursUtils.firstAvailablePickupDate(this.currentStore, now);
    const minISO = StoreHoursUtils.toISO(earliest);

    if ( this.dateInput ) {
      this.dateInput.min = minISO;
      this.dateInput.disabled = false;

      const savedDate = this.dateInput.value;
      const savedValid = savedDate
        && savedDate >= minISO
        && ! StoreHoursUtils.isPickupClosedOnDate(this.currentStore, StoreHoursUtils.parseISODate(savedDate));

      if ( resetSelections || ! savedValid ) {
        this.dateInput.value = minISO;
        this.saveAttribute('Pickup date', minISO);
      }
    }

    if ( resetSelections ) {
      this.viewMonth = null;
    }

    this.renderDatePicker(now);

    this.rebuildTimeWindows(this.getTimeWindowsForSelectedDate(now), resetSelections);
    this.saveAttribute('Preferred store', this.currentStore.handle || this.currentStore.name);
    if ( this.preferredStoreInput ) {
      this.preferredStoreInput.value = this.currentStore.name;
    }
    if ( this.storeSelect && this.currentStore.handle ) {
      this.storeSelect.value = this.currentStore.handle;
    }

    this.setStatus('');
    this.notifyChange();
  }

  showNoStoreState() {
    if ( this.storeRow ) {
      this.storeRow.hidden = true;
    }
    if ( this.storeLabel ) {
      this.storeLabel.hidden = true;
    }
    if ( this.noStorePrompt ) {
      this.noStorePrompt.hidden = false;
    }
    if ( this.dateInput ) {
      this.dateInput.value = '';
      this.dateInput.disabled = true;
      this.dateInput.removeAttribute('min');
    }
    if ( this.datePicker ) {
      this.datePicker.hidden = true;
    }
    this.viewMonth = null;
    if ( this.timeSelect ) {
      this.timeSelect.value = '';
      this.timeSelect.disabled = true;
    }
    if ( this.timeField ) {
      this.timeField.hidden = true;
    }
    this.setStatus(this.noStoreMessage);
  }

  showStoreState(store) {
    const useInlineStoreSelect = Boolean(this.storeSelect);

    if ( this.storeRow ) {
      this.storeRow.hidden = useInlineStoreSelect;
    }
    if ( this.storeLabel ) {
      if ( useInlineStoreSelect ) {
        this.storeLabel.hidden = true;
      } else {
        this.storeLabel.textContent = `Pickup at ${store.name}`;
        this.storeLabel.hidden = false;
      }
    }
    if ( this.noStorePrompt ) {
      this.noStorePrompt.hidden = true;
    }
    if ( this.dateInput ) {
      this.dateInput.disabled = false;
    }
  }

  rebuildTimeWindows(windows, resetSelections) {
    if ( ! this.timeSelect || ! this.timeField ) {
      return;
    }

    const saved = this.timeSelect.value;
    this.timeSelect.innerHTML = '<option value="">Select a time</option>';

    if ( ! windows.length ) {
      this.timeField.hidden = true;
      this.timeSelect.disabled = true;
      this.timeSelect.required = false;
      this.timeSelect.value = '';
      this.saveAttribute('Pickup time', '');
      return;
    }

    this.timeField.hidden = false;
    this.timeSelect.disabled = false;
    if ( this.required ) {
      this.timeSelect.required = true;
    }

    windows.forEach(window => {
      const option = document.createElement('option');
      option.value = window;
      option.textContent = window;
      this.timeSelect.appendChild(option);
    });

    if ( ! resetSelections && saved && windows.includes(saved) ) {
      this.timeSelect.value = saved;
    } else {
      this.timeSelect.value = '';
      this.saveAttribute('Pickup time', '');
    }
  }

  changeMonth(delta) {
    if ( ! this.viewMonth || ! this.currentStore ) {
      return;
    }

    this.viewMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + delta, 1);
    this.renderDatePicker();
  }

  renderDatePicker(now = new Date()) {
    if ( ! this.datePicker || ! this.datePickerDays || ! this.currentStore || typeof StoreHoursUtils === 'undefined' ) {
      if ( this.datePicker ) {
        this.datePicker.hidden = true;
      }
      return;
    }

    const store = this.currentStore;
    const minDate = StoreHoursUtils.firstAvailablePickupDate(store, now);
    const maxDate = StoreHoursUtils.maxPickupDate(store, minDate);
    const minISO = StoreHoursUtils.toISO(minDate);
    const maxISO = StoreHoursUtils.toISO(maxDate);
    const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

    if ( ! this.viewMonth ) {
      const selected = this.dateInput?.value
        ? StoreHoursUtils.parseISODate(this.dateInput.value)
        : minDate;
      this.viewMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
    }

    if ( StoreHoursUtils.monthIndex(this.viewMonth) < StoreHoursUtils.monthIndex(minMonth) ) {
      this.viewMonth = new Date(minMonth.getTime());
    } else if ( StoreHoursUtils.monthIndex(this.viewMonth) > StoreHoursUtils.monthIndex(maxMonth) ) {
      this.viewMonth = new Date(maxMonth.getTime());
    }

    this.datePicker.hidden = false;
    this.datePickerMonth.textContent = new Intl.DateTimeFormat('en-AU', {
      month: 'long',
      year: 'numeric',
      timeZone: store.timezone || 'Australia/Adelaide',
    }).format(this.viewMonth);

    if ( this.datePickerPrev ) {
      this.datePickerPrev.disabled = StoreHoursUtils.monthIndex(this.viewMonth) <= StoreHoursUtils.monthIndex(minMonth);
    }
    if ( this.datePickerNext ) {
      this.datePickerNext.disabled = StoreHoursUtils.monthIndex(this.viewMonth) >= StoreHoursUtils.monthIndex(maxMonth);
    }

    const firstDay = this.viewMonth.getDay();
    const daysInMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + 1, 0).getDate();
    const fragment = document.createDocumentFragment();

    for ( let i = 0; i < firstDay; i += 1 ) {
      const empty = document.createElement('span');
      empty.className = 'pickup-date-picker__day pickup-date-picker__day--empty';
      empty.setAttribute('aria-hidden', 'true');
      fragment.appendChild(empty);
    }

    for ( let day = 1; day <= daysInMonth; day += 1 ) {
      const date = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth(), day);
      const iso = StoreHoursUtils.toISO(date);
      const unavailable = iso < minISO || iso > maxISO || StoreHoursUtils.isPickupClosedOnDate(store, date);
      const isSelected = iso === this.dateInput?.value;

      if ( unavailable ) {
        const cell = document.createElement('span');
        cell.className = 'pickup-date-picker__day pickup-date-picker__day--disabled';
        cell.textContent = String(day);
        cell.setAttribute('role', 'gridcell');
        cell.setAttribute('aria-disabled', 'true');
        fragment.appendChild(cell);
      } else {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pickup-date-picker__day';
        if ( isSelected ) {
          button.classList.add('pickup-date-picker__day--selected');
        }
        button.textContent = String(day);
        button.dataset.pickupDateDay = iso;
        button.setAttribute('role', 'gridcell');
        button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        button.addEventListener('click', () => this.selectDate(iso));
        fragment.appendChild(button);
      }
    }

    this.datePickerDays.replaceChildren(fragment);
  }

  selectDate(iso) {
    if ( ! this.dateInput ) {
      return;
    }

    this.dateInput.value = iso;
    this.onDateChange();
    this.renderDatePicker();
  }

  onDateChange() {
    if ( ! this.dateInput || ! this.currentStore ) {
      return;
    }

    const value = this.dateInput.value;
    if ( ! value ) {
      return;
    }

    const selected = StoreHoursUtils.parseISODate(value);
    const minDate = this.dateInput.min
      ? StoreHoursUtils.parseISODate(this.dateInput.min)
      : StoreHoursUtils.firstAvailablePickupDate(this.currentStore);

    if ( value < StoreHoursUtils.toISO(minDate) || StoreHoursUtils.isPickupClosedOnDate(this.currentStore, selected) ) {
      this.setStatus(this.closedMessage);
      this.dateInput.value = StoreHoursUtils.toISO(minDate);
    } else {
      this.setStatus('');
    }

    this.saveAttribute('Pickup date', this.dateInput.value);
    this.rebuildTimeWindows(this.getTimeWindowsForSelectedDate(), false);
    this.notifyChange();
    this.renderDatePicker();
  }

  setStatus(message) {
    if ( ! this.status ) {
      return;
    }
    this.status.textContent = message;
    this.status.classList.toggle('pickup-scheduler__status--error', Boolean(message));
  }

  saveAttribute(key, value) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const attributes = {};
    attributes[key] = value;

    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).catch(() => {
      /* Non-blocking: form fields still submit at checkout. */
    });
  }
}

if ( ! customElements.get('pickup-scheduler') ) {
  customElements.define('pickup-scheduler', PickupScheduler);
}
